import {useCallback, useEffect, useState} from 'react';
import Container from 'typedi';
import {useTranslation} from 'react-i18next';
import {Button, Layout, MenuProps, Upload, UploadFile} from 'antd';
import {Link, Outlet, useLocation, useNavigate} from 'react-router-dom';
import {i18nKey} from '@/locales/i18n';
import useMessage from '@/hooks/message_api';
import IcUser from '@/assets/icons/ic_user.svg';
import {MenuInfo} from 'rc-menu/lib/interface';
import MenuBar from '@/components/MenuBar/MenuBar';
import {UploadChangeParam} from 'antd/es/upload';
import HeaderLayout from '@/components/HeaderLayout';
import {useFileList} from '@/contexts/FileContext';
import IcBilling from '@/assets/icons/ic_billing.svg';
// import IcSetting from '@/assets/icons/ic_setting.svg';
import IcHistory from '@/assets/icons/ic_history.svg';
import IcSupport from '@/assets/icons/ic_support.svg';
import {AuthService, UsageService} from '@/services';
import IcDocument from '@/assets/icons/ic_document.svg';
import IcArrowDown from '@/assets/icons/ic_arrow_down.svg';
import {GetListUsageCurentDTO, LogoutDTO} from '@/shared';
import IcUserActive from '@/assets/icons/ic_user_active.svg';
import IcArrowRight from '@/assets/icons/ic_arrow_right.svg';
import IcAllDocument from '@/assets/icons/ic_all_document.svg';
import {getItem, handleCustomRequest} from '@/utils/common.util';
import IcBillingActive from '@/assets/icons/ic_billing_active.svg';
import IcDocumentActive from '@/assets/icons/ic_document_active.svg';
import IcAllDocumentActive from '@/assets/icons/ic_all_document_active.svg';
import {ReactComponent as IcUpload} from '@/assets/icons/ic_new_upload.svg';
import {
  IRenderSubIcon,
  IResponseGetUsageCurrent,
  IUserInfo,
  TPageRouteType,
} from '@/types';
import {
  DEFAULT_KEY_ROUTER,
  HTTP_CODE,
  ITEM_KEY_INFO,
  ITEM_KEYS,
  METHOD,
  PageRoute,
  ROLE,
  ROUTER_FROM_ADMIN,
  ROUTER_FROM_USER,
  ROUTER_MAP,
  ROUTER_SUB_MAP_ADMIN,
  ROUTER_SUB_MAP_USER,
  STORAGE_KEYS,
  SUB_MENU,
} from '@/constants';
import styles from './MainLayout.module.scss';
import IcData from '@/assets/icons/ic_data.svg';
import {io} from 'socket.io-client';
import {FILE_UPLOAD_CONFIGURATION} from '@/constants/common/request';
import { PartnerService } from '@/services/core/partner.service';

const {Content, Sider} = Layout;

const MainLayout = () => {
  const partnerService = Container.get(PartnerService);
  const [isSupportWorkflows, setSupportWorkflows] = useState(false);
  const [isSupportMetadata, setSupportMetadata] = useState(false);
  const [isSupportMBAL, setSupportMBAL] = useState(false);
  const message = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const authService = Container.get(AuthService);
  const usageService = Container.get(UsageService);
  const {t} = useTranslation();
  const [idRole, setIdRole] = useState<ROLE>();
  const [openKeyChanges, setOpenKeyChanges] = useState<string[]>([]);
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(null);
  const [usageCurrent, setUsageCurrent] = useState<IResponseGetUsageCurrent>({
    pageUsed: 0,
    totalBlockRegistered: 1,
    totalBlockAllUsed: 0,
    totalBlockUsedAbove85: 0,
  });
  const [collapsed, setCollapsed] = useState(false);
  const isActiveGroup = (key: string) => openKeyChanges.includes(key);
  const {
    fileListState,
    setFileListState,
    setIsOpenProcessing,
    setProgressPercent,
    setListFileError,
    form,
    isCurrentUsage,
  } = useFileList();

  useEffect(()=>{
    setSupportWorkflows(partnerService.isSupportWorkflows());
    setSupportMetadata(partnerService.isSupportMetadata());
    setSupportMBAL(partnerService.isSupportMBAL());
    partnerService.addPermissionUpdateListener(() => {
      setSupportWorkflows(partnerService.isSupportWorkflows());
      setSupportMetadata(partnerService.isSupportMetadata());
      setSupportMBAL(partnerService.isSupportMBAL());
    });
  }, []);

  const fetchUsageUser = async () => {
    try {
      const response = await usageService.getListUsageCurentDTO(
        new GetListUsageCurentDTO(),
      );
      if (
        response.msgSts?.status === HTTP_CODE.OK ||
        response.msgSts?.status === HTTP_CODE.CREATED
      ) {
        const {data} = response;
        setUsageCurrent({
          pageUsed: data.pageUsed ?? 0,
          totalBlockRegistered: data.totalBlockRegistered ?? 1,
          totalBlockAllUsed: data.totalBlockAllUsed ?? 0,
          totalBlockUsedAbove85: data.totalBlockUsedAbove85 ?? 0,
        });
      } else {
        throw Error;
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (idRole === ROLE.PARTNER && !isCurrentUsage) return;
    fetchUsageUser();
  }, [isCurrentUsage]);

  useEffect(() => {
    try {
      const roleIdLocal = localStorage.getItem(STORAGE_KEYS.ROLE_ID) as string;
      const info: IUserInfo = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.USER_INFO) as string,
      );
      if (!roleIdLocal || !info?.name || !info?.username) {
        localStorage.removeItem(STORAGE_KEYS.USER_INFO);
        localStorage.removeItem(STORAGE_KEYS.USER_ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ROLE_ID);
        navigate(PageRoute.LOGIN);
      } else {
        setIdRole(JSON.parse(roleIdLocal));
        setUserInfo(info);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const getSelectedKeys = useCallback((): string[] => {
    const matchedRoute = (Object.keys(ROUTER_MAP) as TPageRouteType[]).find(
      (route) => location.pathname.includes(route),
    );
    return matchedRoute ? [ROUTER_MAP[matchedRoute]] : [];
  }, [location.pathname]);

  const isActivateItem = (key: string) => getSelectedKeys().includes(key);

  const isPartner = idRole && idRole === ROLE.PARTNER;

  const renderStyleGroup = (key: string) =>
    isActiveGroup(key) ? styles.titleGroupActive : styles.titleGroupItem;

  const renderIconActive = (key: string, iconActive: string, icon: string) =>
    isActiveGroup(key) ? iconActive : icon;

  const renderStyleItem = (key: string) =>
    isActivateItem(key) ? styles.itemActivate : styles.item;

  const items: MenuProps['items'] = isPartner
    ? [
        getItem({
          label: (
            <div className={`${renderStyleGroup(SUB_MENU.sub1)}`}>
              {t(i18nKey.menuBar.admin.billing)}
            </div>
          ),
          key: SUB_MENU.sub1,
          icon: (
            <img
              src={renderIconActive(SUB_MENU.sub1, IcBillingActive, IcBilling)}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [
            getItem({
              label: (
                <Link to={PageRoute.USAGE}>
                  <div className={`${renderStyleItem(ITEM_KEYS.usage)}`}>
                    {t(i18nKey.menuBar.admin.usage)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.usage,
            }),
            getItem({
              label: (
                <Link to={PageRoute.INVOICE}>
                  <div className={`${renderStyleItem(ITEM_KEYS.invoice)}`}>
                    {t(i18nKey.menuBar.admin.invoice)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.invoice,
            }),
          ],
        }),
        {
          label: (
            <div className={`${renderStyleGroup(SUB_MENU.sub2)}`}>
              {t(i18nKey.menuBar.admin.orgStructure)}
            </div>
          ),
          key: SUB_MENU.sub2,
          icon: (
            <img
              src={renderIconActive(SUB_MENU.sub2, IcUserActive, IcUser)}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [
            {
              label: (
                <Link to={PageRoute.DEPARTMENTS}>
                  <div className={`${renderStyleItem(ITEM_KEYS.department)}`}>
                    {t(i18nKey.menuBar.admin.departments)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.department,
            },
            {
              label: (
                <Link to={PageRoute.USERS}>
                  <div className={`${renderStyleItem(ITEM_KEYS.user)}`}>
                    {t(i18nKey.menuBar.admin.users)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.user,
            },
          ],
        },
        getItem({
          label: (
            <Link
              to={PageRoute.DOCUMENT_TEMPLATE}
              className={`${renderStyleGroup(SUB_MENU.sub3)}`}
            >
              {t(i18nKey.menuBar.documentTemplate)}
            </Link>
          ),
          key: SUB_MENU.sub3,
          icon: (
            <img
              src={renderIconActive(
                SUB_MENU.sub3,
                IcDocumentActive,
                IcDocument,
              )}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [],
        }),
        getItem({
          label: (
            <Link
              to={PageRoute.WORKFLOW}
              className={`${renderStyleGroup(SUB_MENU.sub9)}`}
            >
              {t(i18nKey.menuBar.user.workFlow)}
            </Link>
          ),
          key: SUB_MENU.sub9,
          icon: (
            <img
              src={renderIconActive(SUB_MENU.sub9, IcBillingActive, IcBilling)}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [
            // getItem({
            //   label: (
            //     <Link to={PageRoute.REQUEST_WORKFLOW}>
            //       <div
            //         className={`${isActivateItem(ITEM_KEYS.request_work_flow) ? styles.itemActivate : styles.item}`}
            //       >
            //         {t(i18nKey.menuBar.user.workFlowRequest)}
            //       </div>
            //     </Link>
            //   ),
            //   key: ITEM_KEYS.request_work_flow,
            // }),
            // getItem({
            //   label: (
            //     <Link to={PageRoute.WORKFLOW}>
            //       <div
            //         className={`${isActivateItem(ITEM_KEYS.work_flow) ? styles.itemActivate : styles.item}`}
            //       >
            //         {t(i18nKey.menuBar.user.workFlows)}
            //       </div>
            //     </Link>
            //   ),
            //   key: ITEM_KEYS.work_flow,
            // }),
          ],
          hidden: !isSupportWorkflows,
        }),
        // getItem(
        //   <div
        //     className={`${isActiveGroup(SUB_MENU.sub4) ? styles.titleGroupActive : styles.titleGroupItem}`}
        //   >
        //     {t(i18nKey.menuBar.admin.configuration)}
        //   </div>,
        //   SUB_MENU.sub4,
        //   <img src={IcSetting} width={22} height={22} />,
        //   [
        //     getItem(
        //       <Link to={PageRoute.CONFIG_BILLING}>
        //         <div
        //           className={`${isActivateItem(ITEM_KEYS.config_billing) ? styles.itemActivate : styles.item}`}
        //         >
        //           {t(i18nKey.menuBar.admin.configuration)}
        //         </div>
        //       </Link>,
        //       ITEM_KEYS.config_billing,
        //     ),
        //   ],
        // ),
        getItem({
          label: (
            <Link
              to={PageRoute.METADATA}
              className={`${renderStyleGroup(SUB_MENU.sub11)}`}
            >
              Metadata
            </Link>
          ),
          key: SUB_MENU.sub11,
          icon: <img src={IcData} width={22} height={22} alt="" />,
          children: [],
          hidden: !isSupportMetadata,
        }),
        getItem({
          label: (
            <Link
              to={PageRoute.LOGGING}
              className={`${renderStyleGroup(SUB_MENU.sub12)}`}
            >
              {t(i18nKey.menuBar.admin.logging)}
            </Link>
          ),
          key: SUB_MENU.sub12,
          icon: (
            <img
              src={renderIconActive(SUB_MENU.sub12, IcBillingActive, IcBilling)}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [],
        }),
        getItem({
          label: (
            <div className={`${renderStyleGroup(SUB_MENU.sub13)}`}>
              {t(i18nKey.menuBar.admin.MBAL)}
            </div>
          ),
          key: SUB_MENU.sub13,
          icon: (
            <img
              src={renderIconActive(SUB_MENU.sub13, IcBillingActive, IcBilling)}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [
            getItem({
              label: (
                <Link to={PageRoute.MBAL_DOCSET_MAP}>
                  <div className={`${renderStyleItem(ITEM_KEYS.docset_map)}`}>
                    {t(i18nKey.menuBar.admin.docsetMap)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.docset_map,
            }),
            getItem({
              label: (
                <Link to={PageRoute.MBAL_TEMPLATE_MAP}>
                  <div className={`${renderStyleItem(ITEM_KEYS.template_map)}`}>
                    {t(i18nKey.menuBar.admin.templateMap)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.template_map,
            }),
          ],
          hidden: !isSupportMBAL,
        }),
      ]
    : [
        getItem({
          label: (
            <Link
              to={PageRoute.PROCESSING_LIST}
              className={`${renderStyleGroup(SUB_MENU.sub5)}`}
            >
              {t(i18nKey.menuBar.user.allDocument)}
            </Link>
          ),
          key: SUB_MENU.sub5,
          icon: (
            <img
              src={renderIconActive(
                SUB_MENU.sub5,
                IcAllDocumentActive,
                IcAllDocument,
              )}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [],
        }),
        getItem({
          label: (
            <Link
              to={PageRoute.DOCUMENT_TEMPLATE}
              className={`${renderStyleGroup(SUB_MENU.sub6)}`}
            >
              {t(i18nKey.menuBar.documentTemplate)}
            </Link>
          ),
          key: SUB_MENU.sub6,
          icon: (
            <img
              src={renderIconActive(
                SUB_MENU.sub6,
                IcDocumentActive,
                IcDocument,
              )}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [],
        }),
        getItem({
          label: (
            <div className={`${renderStyleGroup(SUB_MENU.sub1)}`}>
              {t(i18nKey.menuBar.user.workFlow)}
            </div>
          ),
          key: SUB_MENU.sub9,
          icon: (
            <img
              src={renderIconActive(SUB_MENU.sub9, IcBillingActive, IcBilling)}
              width={22}
              height={22}
              alt=""
            />
          ),
          children: [
            getItem({
              label: (
                <Link to={PageRoute.REQUEST_WORKFLOW}>
                  <div
                    className={`${renderStyleItem(ITEM_KEYS.request_work_flow)}`}
                  >
                    {t(i18nKey.menuBar.user.workFlowRequest)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.request_work_flow,
            }),
            getItem({
              label: (
                <Link to={PageRoute.WORKFLOW}>
                  <div className={`${renderStyleItem(ITEM_KEYS.work_flow)}`}>
                    {t(i18nKey.menuBar.user.workFlows)}
                  </div>
                </Link>
              ),
              key: ITEM_KEYS.work_flow,
            }),
          ],
          hidden: !isSupportWorkflows,
        }),
        getItem({
          label: (
            <Link
              to={PageRoute.SERVICE_HISTORY}
              className={`${renderStyleGroup(SUB_MENU.sub7)}`}
            >
              {t(i18nKey.menuBar.user.serviceHistory)}
            </Link>
          ),
          key: SUB_MENU.sub7,
          icon: <img src={IcHistory} width={22} height={22} alt="" />,
          children: [],
        }),
        getItem({
          label: (
            <Link
              to={PageRoute.SUPPORT}
              className={`${renderStyleGroup(SUB_MENU.sub8)}`}
            >
              {t(i18nKey.menuBar.user.support)}
            </Link>
          ),
          key: SUB_MENU.sub8,
          icon: <img src={IcSupport} width={22} height={22} alt="" />,
          children: [],
        }),
        getItem({
          label: (
            <Link
              to={PageRoute.METADATA}
              className={`${renderStyleGroup(SUB_MENU.sub10)}`}
            >
              Metadata
            </Link>
          ),
          key: SUB_MENU.sub10,
          icon: <img src={IcData} width={22} height={22} alt="" />,
          children: [],
          hidden: !isSupportMetadata,
        }),
      ];

  useEffect(() => {
    const latestKey =
      openKeyChanges[openKeyChanges.length - DEFAULT_KEY_ROUTER];

    if (latestKey === SUB_MENU.sub0) return;

    if (latestKey) {
      const routes =
        idRole === ROLE.PARTNER
          ? ROUTER_SUB_MAP_ADMIN[latestKey as keyof typeof ROUTER_SUB_MAP_ADMIN]
          : ROUTER_SUB_MAP_USER[latestKey as keyof typeof ROUTER_SUB_MAP_USER];

      if (routes) {
        const groups = new Set(
          routes.map(
            (route) => route.split(PageRoute.HOME)[DEFAULT_KEY_ROUTER],
          ),
        );

        const isRouterNavigate = !routes.some((route) =>
          location.pathname.startsWith(route),
        );

        if (groups.size === DEFAULT_KEY_ROUTER && isRouterNavigate) {
          navigate(routes[0], {
            state: location.state,
          });
        }
      }
    }
  }, [openKeyChanges, idRole, location.pathname, navigate]);

  useEffect(() => {
    const routerSplit = `${PageRoute.HOME}${location.pathname.split(PageRoute.HOME)[DEFAULT_KEY_ROUTER]}`;
    const searchParams = location.search;
    const subMenu =
      idRole === ROLE.PARTNER
        ? ROUTER_FROM_ADMIN[routerSplit as keyof typeof ROUTER_FROM_ADMIN]
        : ROUTER_FROM_USER[routerSplit as keyof typeof ROUTER_FROM_USER];

    if (subMenu === SUB_MENU.sub0) {
      setOpenKeyChanges([SUB_MENU.sub0]);
    }

    if (subMenu && location.pathname) {
      navigate(`${location.pathname}${searchParams}`, {
        state: location.state,
      });
      setOpenKeyChanges([subMenu]);
    } else {
      navigate(PageRoute.HOME);
    }
  }, [location.pathname, idRole, navigate]);

  const logOut = async () => {
    try {
      const {
        data: {message: successMessage},
      } = await authService.logOut(new LogoutDTO());

      [
        STORAGE_KEYS.USER_ACCESS_TOKEN,
        STORAGE_KEYS.USER_INFO,
        STORAGE_KEYS.ROLE_ID,
        STORAGE_KEYS.TEMPLATE_ID,
        STORAGE_KEYS.FILE_LIST,
      ].forEach((key) => localStorage.removeItem(key));

      message.success(successMessage);
      navigate(PageRoute.LOGIN);
    } catch (error) {
      console.error(error);
      message.error(error as string);
    }
  };

  const handleOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.length
      ? [keys[keys.length - DEFAULT_KEY_ROUTER]]
      : [];
    setOpenKeyChanges(latestOpenKey);
  };

  const handleInfoChange = (m: MenuInfo) => {
    if (m.key === ITEM_KEY_INFO.LOG_OUT) {
      logOut();
    } else {
      navigate(PageRoute.ACCOUNT);
    }
  };

  const handleChange = (info: UploadChangeParam<UploadFile<File>>) => {
    const validFiles: UploadFile<File>[] = [];
    const errorFiles: UploadFile<File>[] = [];

    info.fileList.forEach((itemFile) => {
      const isValidType =
        itemFile.type &&
        ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'].includes(
          itemFile.type,
        );
      const isValidSize =
        itemFile.size !== undefined &&
        itemFile.size <= FILE_UPLOAD_CONFIGURATION.FILE_SIZE_MB * 1024 * 1024;

      if (isValidType && isValidSize) {
        validFiles.push(itemFile);
      } else {
        errorFiles.push(itemFile);
      }
    });

    handleCustomRequest({
      onProgress: (ev) => {
        setProgressPercent(ev.percent ?? 0);
      },
      onSuccess: ({status}) => {
        if (status === HTTP_CODE.OK) {
          setTimeout(() => {
            setFileListState(validFiles);
            setListFileError(errorFiles);
            form.setFieldsValue({
              file: validFiles,
            });

            if (info.fileList.length <= 1) {
              form.setFieldsValue({
                name: info.file.name,
              });
            }

            setIsOpenProcessing(false);
            setProgressPercent(0);
          }, 800);
        }
      },
      onError: (error) => {
        setIsOpenProcessing(false);
        setProgressPercent(0);
        console.error('Upload failed:', error);
      },
      file: info.file.originFileObj as File,
      action: '',
      method: METHOD.POST,
    });
  };

  useEffect(() => {
    setFileListState([]);
    setListFileError([]);
    form.resetFields();
  }, [location]);

  useEffect(() => {
    const newSocket = io(`${process.env.REACT_APP_BACKEND}`, {
      transports: ['websocket'],
      auth: {
        token: `${localStorage.getItem(STORAGE_KEYS.USER_ACCESS_TOKEN)}`,
      },
    });

    newSocket.on('updateOcrResult', fetchUsageUser);
    return () => {
      if (newSocket.connected) {
        newSocket.close();
      }
    };
  }, []);

  return (
    <Layout>
      {location.pathname.includes(PageRoute.PROCESSING_REVIEW) ||
      location.pathname.includes(PageRoute.PREVIEW_WORKFLOW) ? null : (
        <HeaderLayout
          roleId={idRole}
          usageCurrent={usageCurrent}
          nameInfo={userInfo?.name}
          isCollapse={collapsed}
          onInfoChange={handleInfoChange}
          toggleMenu={() => setCollapsed(!collapsed)}
        />
      )}
      <Layout>
        <Sider
          width={
            location.pathname.includes(PageRoute.PROCESSING_REVIEW) ||
            location.pathname.includes(PageRoute.PREVIEW_WORKFLOW)
              ? 0
              : 250
          }
          className={styles.idpSlider}
          trigger={null}
          collapsible
          collapsed={collapsed}
        >
          {idRole !== ROLE.PARTNER &&
            (location.pathname.includes(PageRoute.REQUEST) &&
            fileListState.length <= 0 ? (
              <Upload
                className={styles.uploadDocument}
                multiple
                listType="picture"
                accept=".png, .jpg,.jpeg,.pdf"
                beforeUpload={() => {
                  setIsOpenProcessing(true);
                }}
                customRequest={handleCustomRequest}
                showUploadList={false}
                onChange={handleChange}
                maxCount={10}
              >
                <Button
                  className={styles.btnGradient}
                  icon={<IcUpload width={20} height={20} color="white" />}
                >
                  {!collapsed && t(i18nKey.menuBar.user.uploadDocument)}
                </Button>
              </Upload>
            ) : (
              <div className={styles.uploadWrapper}>
                <Button
                  className={styles.btnUploadNavigate}
                  icon={<IcUpload width={20} height={20} color="white" />}
                  onClick={() => {
                    if (location.pathname.includes(PageRoute.REQUEST)) {
                      setFileListState([]);
                    } else {
                      navigate(PageRoute.REQUEST);
                    }
                  }}
                >
                  {!collapsed && t(i18nKey.menuBar.user.uploadDocument)}
                </Button>
              </div>
            ))}
          <MenuBar
            style={{marginTop: idRole !== ROLE.PARTNER ? 24 : 32}}
            expandIcon={({eventKey, isOpen}: IRenderSubIcon) => {
              return (
                idRole === ROLE.PARTNER &&
                ![
                  SUB_MENU.sub3,
                  SUB_MENU.sub11,
                  SUB_MENU.sub9,
                  SUB_MENU.sub12,
                ].includes(eventKey as string) && (
                  <img
                    src={isOpen ? IcArrowDown : IcArrowRight}
                    width={16}
                    height={16}
                    alt=""
                  />
                )
              );
            }}
            openKeys={openKeyChanges}
            selectedKeys={getSelectedKeys()}
            items={items}
            onOpenChange={handleOpenChange}
            triggerSubMenuAction="click"
          />
        </Sider>
        <Content>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
